import { ArrowLeft } from "../../../../common/arrow";
import { FormattedMessage } from "react-intl";
import React from "react";
import { FormControlLabel, Switch, Box, Typography, styled } from "@mui/material";
import { showSuccessToast } from "../../../../../toast/toast-service";
import { useMutation, useQuery } from "@apollo/client";
import { GET_USER_EMAIL_SETTINGS, CHANGE_USER_EMAIL_SETTINGS } from "./queries";
import { PrimarySpinner } from "../../../../spinner/primary-spinner";
import { USER_EMAIL_SETTINGS } from "../../../../../constants";
import _ from "lodash";

export const EmailSettingsBox = ({ setIsEmailMenuOpen }) => {
  const { data: userData, loading: loadingUserData } = useQuery(
    GET_USER_EMAIL_SETTINGS
  );

  const { SUMMARY, NEW_NOTIFICATIONS, NEW_CHAT_MESSAGE } = USER_EMAIL_SETTINGS;

  const [updateEmailsActivated] = useMutation(CHANGE_USER_EMAIL_SETTINGS, {
    refetchQueries: [GET_USER_EMAIL_SETTINGS],
    onCompleted: () => {
      showSuccessToast(
        <FormattedMessage id="notifications.emailSettings.toasts.update-successful" />
      );
    },
  });

  const changeEmailSettings = async (changedOptionKey) => {
    const isOptionAlreadySet =
      userData?.user?.emailSettings.includes(changedOptionKey);

    const newEmailSettings = isOptionAlreadySet
      ? _.without(userData?.user?.emailSettings, changedOptionKey)
      : userData?.user?.emailSettings.concat(changedOptionKey);

    await updateEmailsActivated({
      variables: {
        emailSettings: newEmailSettings,
      },
    });
  };

  const changeNoMailsSwitch = async () => {
    await updateEmailsActivated({
      variables: {
        emailSettings: [],
      },
    });
  };

  return (
    <EmailSettingsContainer>
      <EmailSettingsHeader>
        <ArrowLeft onClick={() => setIsEmailMenuOpen(false)} />
        <EmailSettingsTitle>
          <FormattedMessage id="notifications.emailSettings" />
        </EmailSettingsTitle>
      </EmailSettingsHeader>
      <EmailSettingsContent>
        {loadingUserData && <PrimarySpinner />}
        {!loadingUserData && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    !!userData?.user?.emailSettings?.includes(NEW_NOTIFICATIONS)
                  }
                />
              }
              label={
                <EmailOptionLabel>
                  <FormattedMessage id="notifications.emailSettings.on-new-notifications" />
                </EmailOptionLabel>
              }
              onChange={() => changeEmailSettings(NEW_NOTIFICATIONS)}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!userData.user?.emailSettings?.includes(SUMMARY)}
                  onChange={async () => {
                    await changeEmailSettings(SUMMARY);
                  }}
                />
              }
              label={
                <EmailOptionLabel>
                  <FormattedMessage
                    id={"notifications.emailSettings.summary-mail"}
                  />
                </EmailOptionLabel>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={
                    !!userData.user?.emailSettings?.includes(NEW_CHAT_MESSAGE)
                  }
                  onChange={() => changeEmailSettings(NEW_CHAT_MESSAGE)}
                />
              }
              label={
                <EmailOptionLabel>
                  <FormattedMessage
                    id={"notifications.emailSettings.on-new-chat-messages"}
                  />
                </EmailOptionLabel>
              }
            />
            <FormControlLabel
              control={
                <Switch
                  checked={userData?.user?.emailSettings?.length === 0}
                  disabled={userData?.user?.emailSettings?.length === 0}
                />
              }
              label={
                <EmailOptionLabel>
                  <FormattedMessage
                    id={"notifications.emailSettings.no-mails"}
                  />
                </EmailOptionLabel>
              }
              onChange={() => changeNoMailsSwitch()}
            />
          </>
        )}
      </EmailSettingsContent>
    </EmailSettingsContainer>
  );
};

// Styled Components
const EmailSettingsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minWidth: '400px',
  minHeight: '300px',
  maxWidth: '500px',
  maxHeight: '600px',
}));

const EmailSettingsHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  borderBottom: '1px solid #e0e0e0',
  [theme.breakpoints.down('lg')]: {
    padding: '20px',
  },
}));

const EmailSettingsTitle = styled(Typography)(({ theme }) => ({
  margin: '0 0 0 12px',
  fontWeight: 600,
  fontSize: '19px',
  lineHeight: '26px',
  color: '#383838',
  [theme.breakpoints.down('lg')]: {
    fontSize: '20px',
    lineHeight: '28px',
  },
}));

const EmailSettingsContent = styled(Box)(({ theme }) => ({
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  flex: 1,
  [theme.breakpoints.down('lg')]: {
    padding: '20px',
    gap: '16px',
  },
}));

const EmailOptionLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '14px',
  marginLeft: '8px',
  color: '#383838',
  [theme.breakpoints.down('lg')]: {
    fontSize: '13px',
  },
}));
